import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import FAQSection from '../components/FAQSection';

const CS = () => <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-orange-500 to-yellow-500 text-white shadow-lg animate-pulse">COMING SOON</span>;

const SH = ({ ic, t, d }) => (
  <div className="flex items-center gap-3 mb-6">
    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center text-white text-lg">{ic}</div>
    <div><h3 className="text-xl font-bold text-white">{t}</h3><p className="text-sm text-gray-400">{d}</p><p className="text-xs text-gray-500 mt-1">Illustrative preview &mdash; not live data</p></div>
  </div>
);

const MC = ({ type, h = "h-48" }) => {
  if (type === "line") return <div className={h + " bg-gray-800/30 rounded-lg border border-gray-700/30 flex items-end p-4 gap-1"}>{[35,50,42,60,55,68,62,78,70,82,76,88].map((v,i) => <div key={i} className="flex-1 bg-gradient-to-t from-blue-600 to-blue-400 rounded-t opacity-80" style={{height: v + "%"}} />)}</div>;
  if (type === "stacked") return <div className={h + " bg-gray-800/30 rounded-lg border border-gray-700/30 flex items-end p-4 gap-1"}>{[55,65,50,75,60,72,80,68,85,78,82,92].map((v,i) => <div key={i} className="flex-1 flex flex-col-reverse gap-0.5" style={{height: v + "%"}}><div className="flex-[3] bg-blue-500 rounded-t" /><div className="flex-[2] bg-green-500" /><div className="flex-[1] bg-orange-500 rounded-b" /></div>)}</div>;
  if (type === "bar") return <div className={h + " bg-gray-800/30 rounded-lg border border-gray-700/30 flex items-end p-4 gap-2"}>{[65,40,80,55,35,70,48,85].map((v,i) => <div key={i} className={"flex-1 rounded-t opacity-80 " + (i%2===0?"bg-green-500":"bg-blue-500")} style={{height: v+"%"}} />)}</div>;
  return null;
};

const DragItem = ({ label, active }) => (
  <div className={"flex items-center gap-3 p-3 rounded-lg border transition-all cursor-move " + (active ? "bg-orange-500/10 border-orange-500/40 text-white" : "bg-gray-800/30 border-gray-700/30 text-gray-400 hover:border-gray-600")}>
    <span className="text-gray-500">☰</span>
    <span className="text-sm font-medium">{label}</span>
    {active && <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Added</span>}
  </div>
);

const OppCard = ({ title, rank, vol, gain, effort, effortColor }) => (
  <div className="bg-gray-800/50 border border-gray-700/30 rounded-xl p-5 hover:border-orange-500/30 transition-all">
    <h4 className="font-semibold text-white text-sm">{title}</h4>
    <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
      <div><span className="text-gray-500">Current Rank:</span><span className="text-white ml-1">#{rank}</span></div>
      <div><span className="text-gray-500">Search Vol:</span><span className="text-white ml-1">{vol}/mo</span></div>
      <div><span className="text-gray-500">Est. Traffic Gain:</span><span className="text-green-400 ml-1">+{gain}/mo</span></div>
      <div><span className="text-gray-500">Effort:</span><span className={"ml-1 " + effortColor}>{effort}</span></div>
    </div>
  </div>
);

function MyReports() {

  const REPORTS_FAQ = [
    { q: "What types of reports can I generate?", a: "SERPfinity offers ranking reports, site audit reports, competitive analysis reports, AI visibility reports, content performance reports, and executive summaries." },
    { q: "Can I schedule automated report delivery?", a: "Yes, set up daily, weekly, or monthly automated reports delivered via email to yourself, team members, or clients." },
    { q: "Are white-label reports available?", a: "Agency plans include white-label reporting with custom branding, logos, and colors for professional client deliverables." },
    { q: "What export formats are available?", a: "Export reports in PDF, CSV, or share via secure web-based interactive dashboard links." },
    { q: "How far back does historical data go?", a: "SERPfinity retains historical data for your entire subscription duration for long-term trend analysis." },
    { q: "Can I create custom report templates?", a: "Yes, create and save custom templates for different audiences and schedule them for automated generation." }
  ];

  return (
    <>
      <Helmet>
        <title>SEO Reports | Custom Reporting & Analytics | SERPfinity</title>
        <meta name="description" content="Generate comprehensive SEO and AEO reports with ranking data and AI visibility metrics." />
        <meta name="keywords" content="SEO reports, custom reporting, automated reports, SERPfinity reports" />
        <link rel="canonical" href="https://thedigitalhall.club/reports" />
        <meta name="robots" content="index, follow" />
        <script type="application/ld+json">{JSON.stringify({"@context":"https://schema.org","@type":"FAQPage","mainEntity":REPORTS_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))})}</script>
      </Helmet>
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 via-transparent to-orange-600/10" />
        <div className="max-w-7xl mx-auto px-4 pt-28 pb-16 text-center relative z-10">
          <CS />
          <h1 className="text-5xl md:text-6xl font-extrabold text-white mt-6">My Reports</h1>
          <p className="text-xl text-gray-400 mt-4 max-w-3xl mx-auto">Client and stakeholder reporting that tells the story. Clean, exportable, presentation-ready reports powered by AI insights.</p>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 pb-20">
        <section className="mb-16">
          <SH ic="📚" t="Report Library" d="All your generated reports in one place" />
          <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6 overflow-x-auto">
            <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b border-gray-700">{["Report Name","Client / Project","Channels","Last Generated","Status",""].map(h=><th key={h} className="py-3 px-4 font-medium">{h}</th>)}</tr></thead><tbody>
              {[["Monthly Growth Report","Your client","SEO + SEM","—","Preview","—"],["Weekly Performance","Your client","All channels","—","Preview","—"],["Q1 Executive Summary","Your client","SEO","—","Preview","—"]].map((r,i)=><tr key={i} className="border-b border-gray-800 text-gray-300 hover:bg-gray-800/30">{r.map((c,j)=><td key={j} className={"py-3 px-4 " + (j===5?"text-blue-400 cursor-pointer hover:text-blue-300":"")}>{c}</td>)}</tr>)}
            </tbody></table>
          </div>
        </section>

        <section className="mb-16">
          <SH ic="🎨" t="Report Builder" d="Drag and drop sections to build custom reports" />
          <div className="grid md:grid-cols-3 gap-6">
            <div className="md:col-span-1">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Available Sections</p>
              <div className="space-y-2">
                <DragItem label="+ Executive Summary" active={true} />
                <DragItem label="+ SEO Performance" active={true} />
                <DragItem label="+ Paid Advertising" active={true} />
                <DragItem label="+ Social Impact" active={false} />
                <DragItem label="+ Content Growth" active={false} />
                <DragItem label="+ Technical Health" active={false} />
                <DragItem label="+ Competitive Landscape" active={true} />
              </div>
            </div>
            <div className="md:col-span-2">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-3">Report Preview</p>
              <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6 space-y-6">
                <div className="bg-gray-900/50 rounded-lg p-4 border border-blue-500/20">
                  <h4 className="text-sm font-semibold text-blue-400 mb-2">📋 Executive Summary</h4>
                  <p className="text-sm text-gray-300 leading-relaxed">Your executive summary appears here once a report runs against a connected data source. It describes what actually changed over the period - no figures are shown until there are measured figures to show.</p>
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4 border border-green-500/20">
                  <h4 className="text-sm font-semibold text-green-400 mb-3">📈 SEO Performance</h4>
                  <MC type="line" h="h-32" />
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4 border border-orange-500/20">
                  <h4 className="text-sm font-semibold text-orange-400 mb-3">💰 Paid Advertising</h4>
                  <MC type="bar" h="h-32" />
                </div>
                <div className="bg-gray-900/50 rounded-lg p-4 border border-purple-500/20">
                  <h4 className="text-sm font-semibold text-purple-400 mb-3">🎯 Competitive Landscape</h4>
                  <MC type="stacked" h="h-32" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <SH ic="🏆" t="Competitive Intelligence" d="Share of Voice and content gap analysis" />
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6">
              <h4 className="text-sm font-semibold text-gray-400 mb-4">Share of Voice</h4>
              <MC type="stacked" h="h-48" />
              <div className="flex gap-4 mt-3 justify-center">{[["Your Site","bg-orange-500"],["Competitor A","bg-blue-500"],["Competitor B","bg-green-500"],["Others","bg-gray-500"]].map(([l,c])=><div key={l} className="flex items-center gap-1.5 text-xs text-gray-400"><div className={"w-2.5 h-2.5 rounded "+c}/>{l}</div>)}</div>
            </div>
            <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6">
              <h4 className="text-sm font-semibold text-gray-400 mb-4">Content Gap Map</h4>
              <div className="h-48 bg-gray-800/30 rounded-lg border border-gray-700/30 relative p-4">{[[20,30,14],[40,50,22],[60,25,18],[75,65,26],[35,75,12],[55,40,20],[80,55,16],[25,60,24]].map(([x,y,s],i)=><div key={i} className="absolute rounded-full bg-red-500/30 border-2 border-red-400" style={{left:x+"%",bottom:y+"%",width:s+"px",height:s+"px"}} />)}</div>
              <p className="text-xs text-gray-500 mt-2">Red bubbles = competitor keywords you do not rank for</p>
            </div>
          </div>
        </section>

        <section className="mb-16">
          <SH ic="💵" t="ROI Summary" d="Channel-level return on investment" />
          <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6 overflow-x-auto">
            <table className="w-full text-sm"><thead><tr className="text-left text-gray-500 border-b border-gray-700">{["Channel","Spend","Revenue","ROI","Trend"].map(h=><th key={h} className="py-3 px-4 font-medium">{h}</th>)}</tr></thead><tbody>
              {[["SEO","—","—","—","—","text-gray-400"],["Paid Search","—","—","—","—","text-gray-400"],["Social Media","—","—","—","—","text-gray-400"],["Email","—","—","—","—","text-gray-400"]].map((r,i)=><tr key={i} className="border-b border-gray-800 text-gray-300">{r.slice(0,4).map((c,j)=><td key={j} className="py-3 px-4">{c}</td>)}<td className={"py-3 px-4 font-semibold " + r[5]}>{r[4]}</td></tr>)}
            </tbody></table>
          </div>
        </section>

        <section className="mb-16">
          <SH ic="📅" t="Scheduled Reports" d="Automated delivery on your schedule" />
          <div className="grid md:grid-cols-3 gap-6">
            {[["Weekly Digest","Not scheduled","PDF to email","Not active"],["Monthly Executive","Not scheduled","Google Slides","Not active"],["Quarterly Review","Not scheduled","PDF to email","Not active"]].map(([name,freq,format,status],i)=>(
              <div key={i} className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6">
                <h4 className="font-semibold text-white">{name}</h4>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Frequency:</span><span className="text-gray-300">{freq}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Format:</span><span className="text-gray-300">{format}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Status:</span><span className="text-gray-300">{status}</span></div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-16">
          <SH ic="💡" t="Growth Opportunities Engine" d="AI-powered recommendations with estimated impact" />
          <div className="bg-gradient-to-r from-green-500/5 to-blue-500/5 rounded-xl border border-green-500/20 p-6 mb-6">
            <p className="text-sm text-gray-400">Our AI analyzes all your data and produces actionable opportunities with estimated traffic gain, revenue impact, and effort scoring.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <OppCard title="Update article: Pelvic Floor Therapy Guide" rank="11" vol="8,100" gain="3,400" effort="Low" effortColor="text-green-400" />
            <OppCard title="Create FAQ schema for Product Pages" rank="6" vol="12,400" gain="2,800" effort="Low" effortColor="text-green-400" />
            <OppCard title="Build topical cluster: AI Search" rank="15" vol="22,000" gain="5,200" effort="Medium" effortColor="text-yellow-400" />
            <OppCard title="Optimize Core Web Vitals on /blog" rank="8" vol="6,500" gain="1,900" effort="Medium" effortColor="text-yellow-400" />
          </div>
        </section>

        <section className="mb-16">
          <SH ic="🔮" t="Predictive Intelligence" d="What will happen next" />
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6 text-center">
              <h4 className="text-sm font-semibold text-gray-400 mb-3">Traffic Forecast</h4>
              <MC type="line" h="h-32" />
              <p className="text-xs text-gray-500 mt-2">Predicted 90-day organic traffic trajectory</p>
            </div>
            <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6 text-center">
              <h4 className="text-sm font-semibold text-gray-400 mb-3">Ranking Probability</h4>
              <div className="space-y-3 mt-4">{[["Top 3",0,"bg-gray-600"],["Top 10",0,"bg-gray-600"],["Featured Snippet",0,"bg-gray-600"]].map(([l,v,c])=><div key={l}><div className="flex justify-between text-xs mb-1"><span className="text-gray-400">{l}</span><span className="text-white">{v}%</span></div><div className="h-2 bg-gray-700 rounded-full"><div className={"h-full rounded-full "+c} style={{width:v+"%"}}/></div></div>)}</div>
            </div>
            <div className="bg-gray-800/30 rounded-xl border border-gray-700/30 p-6 text-center">
              <h4 className="text-sm font-semibold text-gray-400 mb-3">ROI Forecast</h4>
              <div className="text-3xl font-bold text-green-400 mt-4">—</div>
              <p className="text-xs text-gray-500 mt-1">Projected 90-day revenue</p>
              <div className="text-sm text-green-400 mt-2">Forecast available once a data source is connected</div>
            </div>
          </div>
        </section>

        <section className="text-center py-16">
          <div className="bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-orange-500/10 rounded-2xl border border-blue-500/20 p-12">
            <CS />
            <h2 className="text-3xl font-bold text-white mt-6">Reporting That Tells the Story</h2>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">Stop showing raw data. Start showing insights, predictions, and ROI. My Reports turns your data into compelling narratives.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/" className="px-6 py-3 bg-gradient-to-r from-orange-500 to-yellow-500 text-white font-semibold rounded-lg hover:shadow-lg transition-all">Back to Home</Link>
              <Link to="/projects" className="px-6 py-3 bg-gray-800 text-white font-semibold rounded-lg border border-gray-700 hover:border-orange-500/50 transition-all">Preview Projects</Link>
            </div>
          </div>
        </section>
      </div>
    </div>
      <FAQSection items={REPORTS_FAQ} />
    </>
  );
}
export default MyReports;